import { BaseView } from '@/models/BaseView';
import { BaseListRequestParams } from '@/models/BaseRequestParams';

export type PageParam = number | string | undefined;

/**
 * Extracts the next page parameter from an API response.
 * Returns a cursor string for keyset pagination, a skip number for offset pagination, or undefined when there are no more pages.
 */
export function getNextPageSkipOrCursor(lastPage: BaseView): PageParam {
  if (lastPage.Cursor) return lastPage.Cursor;
  return lastPage.NextPageSkip;
}

/**
 * Builds pagination query parameters based on the page param type.
 * - `undefined`: initial page request — sends only `{ take }` with no skip or cursor
 * - `string`: cursor-based pagination — sends `{ cursor, take }`
 * - `number`: skip-based pagination — sends `{ skip, take }`
 */
export function buildPaginationParams(pageParam: PageParam, take: number): BaseListRequestParams {
  if (typeof pageParam === 'string') return { cursor: pageParam, take };
  if (typeof pageParam === 'number') return { skip: pageParam, take };
  return { take };
}

/**
 * De-duplicates flattened infinite-query items by Id, keeping the first occurrence.
 * Skip-paginated lists re-return items from earlier pages when the server-side order
 * shifts between page fetches; duplicate Ids collapse FlashList's stableId-keyed
 * recycling (two indices share one ViewHolder), which renders blank cells.
 * @param items - Flattened page items, possibly containing duplicates or empty entries
 */
export function dedupeById<T extends { Id: number }>(items: (T | null | undefined)[]): T[] {
  const seen = new Set<number>();
  const unique = items.filter((item): item is T => {
    if (!item || seen.has(item.Id)) return false;
    seen.add(item.Id);
    return true;
  });
  if (__DEV__ && unique.length < items.length) {
    console.warn(`dedupeById: dropped ${items.length - unique.length} duplicate/empty item(s)`);
  }
  return unique;
}
